/**
 * DiscoveryBoardBridge — postMessage bridge between the Discovery Board iframe
 * (ReactFlow, served by SeivaWeb.DiscoveryBoardController at /discovery-board)
 * and the host DiscoveryGuestLive. Mirrors WorkflowEditorBridge.
 *
 * Protocol v:1:
 *   Board → LiveView:  {v:1, type:"ready"} | {v:1, type:"graph_changed", graph, dirty}
 *                      | {v:1, type:"save", graph} | {v:1, type:"node_selected", node_id}
 *                      | {v:1, type:"board_error", scope, message, stack, graph_version}
 *   LiveView → Board:  {v:1, type:"load_graph", graph, editable, graph_version, active_journey, active_process_view, locale}
 *                      | {v:1, type:"focus_journey", active_journey}
 *                      | {v:1, type:"focus_process_view", active_process_view}
 *                      | {v:1, type:"auto_layout"}
 *                      | {v:1, type:"save_ack", graph_version}
 *                      | {v:1, type:"save_error", reason}
 *                      | {v:1, type:"style_vars", vars}
 *                      | {v:1, type:"ping"}
 *
 * Element: <iframe data-origin="<origin|*>" phx-hook="DiscoveryBoardBridge">.
 * LiveView pushes via push_event(socket, "discovery_board:push", %{type: "...", ...}).
 */
const DiscoveryBoardBridge = {
  mounted() {
    this.targetOrigin = this.el.dataset.origin || "*";
    this.editorReady = false;
    this.pendingPushes = [];
    this.onMessage = (event) => this.handleInbound(event);
    window.addEventListener("message", this.onMessage);

    this.themeObserver = new MutationObserver(() => {
      if (this.editorReady) this.postStyleVars();
    });
    this.themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class", "style"],
    });

    this.handleEvent("discovery_board:push", (payload) => {
      if (!this.editorReady && payload) {
        this.pendingPushes.push(payload);
        return;
      }
      this.postToBoard(payload);
    });

    // Depois que a IA reescreve o grafo, o frame precisa montar DE NOVO: a
    // árvore React que quebrou já desmontou e não recebe mais nada.
    this.handleEvent("discovery_board:reload", () => this.reloadFrame());

    this.startReadyWatchdog();
  },

  reloadFrame() {
    this.editorReady = false;
    this.pendingPushes = [];
    this.startReadyWatchdog();
    try {
      this.el.contentWindow.location.reload();
    } catch {
      this.el.src = this.el.src;
    }
  },

  destroyed() {
    window.removeEventListener("message", this.onMessage);
    this.themeObserver?.disconnect();
    this.clearReadyWatchdog();
    if (this.debTimer) clearTimeout(this.debTimer);
  },

  updated() {
    const newOrigin = this.el.dataset.origin;
    if (newOrigin) this.targetOrigin = newOrigin;
    // O host reseta `board_ready?` ao trocar de aba, mas o board só chama
    // `onReady` uma vez por vida do iframe. Quando o DOM patching PRESERVA o
    // iframe, ninguém reenvia o handshake e o overlay de loading fica eterno
    // sobre um board perfeitamente vivo — reafirmá-lo aqui resolve.
    //
    // O gate é `data-host-ready` (atributo, que `phx-update="ignore"` continua
    // atualizando): sem ele o reanúncio vira LAÇO — o host assina `ready`,
    // re-renderiza, dispara `updated()`, e recomeça.
    if (this.editorReady && this.el.dataset.hostReady === "false") {
      this.pushEvent("discovery_board:ready", {});
    }
  },

  /**
   * O `ready` é a ÚNICA coisa que tira o overlay de loading da sala. Se ele não
   * chega, o usuário fica olhando um spinner sem fim e sem causa. Três motivos
   * reais já vistos: o bundle do board não compilou (o controller serve um
   * fallback HTML com 200 e SEM bridge), o iframe foi preservado e o board não
   * repete o handshake, ou o render lançou. O watchdog cobre os três: primeiro
   * pergunta (o board vivo responde), depois desiste e avisa a sala.
   */
  startReadyWatchdog() {
    this.clearReadyWatchdog();
    this.pingTimer = setTimeout(() => {
      if (this.editorReady) return;
      this.postToBoard({ type: "ping" });
    }, 6000);
    this.stuckTimer = setTimeout(() => {
      if (this.editorReady) return;
      this.pushEvent("discovery_board:stuck", {});
    }, 14000);
  },

  clearReadyWatchdog() {
    if (this.pingTimer) clearTimeout(this.pingTimer);
    if (this.stuckTimer) clearTimeout(this.stuckTimer);
    this.pingTimer = null;
    this.stuckTimer = null;
  },

  handleInbound(event) {
    if (!event.source || event.source !== this.el.contentWindow) return;
    if (this.targetOrigin !== "*" && event.origin !== this.targetOrigin) return;
    const msg = event.data;
    if (!msg || msg.v !== 1 || typeof msg.type !== "string") return;

    switch (msg.type) {
      case "ready":
        this.editorReady = true;
        this.clearReadyWatchdog();
        this.pushEvent("discovery_board:ready", {});
        for (const payload of this.pendingPushes.splice(0)) this.postToBoard(payload);
        this.postStyleVars();
        break;
      case "graph_changed":
        this.pushEventDebounced("discovery_board:graph_changed", { graph: msg.graph }, 1500);
        break;
      case "save":
        if (this.debTimer) {
          clearTimeout(this.debTimer);
          this.debTimer = null;
        }
        this.pushEvent("discovery_board:save", { graph: msg.graph });
        break;
      case "node_selected":
        this.pushEvent("discovery_board:node_selected", { node_id: msg.node_id });
        break;
      case "board_error":
        // O board morreu de pé. Tirar o loading é obrigação do host; o `scope`
        // diz se o conserto cabe à IA (grafo) ou é bug do board (render).
        this.clearReadyWatchdog();
        this.pushEvent("discovery_board:error", {
          scope: msg.scope === "graph" ? "graph" : "render",
          message: String(msg.message || "").slice(0, 800),
          stack: String(msg.stack || "").slice(0, 4000),
          graph_version: typeof msg.graph_version === "number" ? msg.graph_version : null,
        });
        break;
    }
  },

  // Variável do board → papéis do host, em ordem de preferência (o primeiro
  // definido vence). Styleguide 11c.5: acento blue-500, fundo gray-100
  // (superfície rebaixada), texto navy-500, fios gray-200/300, raio 8. Os
  // papéis do DS (`--surface-*`, `--border-hairline`, `--color-blue-500`,
  // `--radius-control`) vêm primeiro; os nomes legados ficam de fallback para
  // o host que ainda não carrega a camada AIOS.
  STYLE_VAR_SOURCES: {
    "--accent-primary": ["--color-blue-500", "--accent-primary"],
    "--bg-primary": ["--surface-page", "--bg-primary"],
    "--bg-secondary": ["--surface-sunken", "--bg-secondary"],
    "--bg-card": ["--surface-card", "--bg-card"],
    "--text-primary": ["--text-primary"],
    "--text-secondary": ["--text-secondary"],
    "--text-tertiary": ["--text-tertiary"],
    "--border-default": ["--border-hairline", "--border-default"],
    "--border-strong": ["--border-strong"],
    "--radius-sm": ["--radius-control"],
  },

  postStyleVars() {
    const cs = getComputedStyle(document.documentElement);
    const vars = {};
    for (const [name, sources] of Object.entries(this.STYLE_VAR_SOURCES)) {
      for (const source of sources) {
        const value = cs.getPropertyValue(source).trim();
        if (value && !value.includes("var(")) {
          vars[name] = value;
          break;
        }
      }
    }
    if (Object.keys(vars).length > 0) this.postToBoard({ type: "style_vars", vars });
  },

  postToBoard(payload) {
    const iframe = this.el;
    if (!iframe.contentWindow) return;
    try {
      iframe.contentWindow.postMessage({ v: 1, ...payload }, this.targetOrigin);
    } catch (err) {
      console.error("[DiscoveryBoardBridge] postMessage failed:", err);
    }
  },

  pushEventDebounced(name, payload, ms) {
    if (this.debTimer) clearTimeout(this.debTimer);
    this.debTimer = setTimeout(() => {
      this.pushEvent(name, payload);
      this.debTimer = null;
    }, ms);
  },
};

export default DiscoveryBoardBridge;
